import {
  PRICE_LIST_CATEGORIES,
  PRICE_LIST_CATEGORY_LABELS,
  PRICE_LIST_UNIT_LABELS,
  type PriceListCategory,
  type QuoteCatalogItem,
  type XopQuoteRequestLine,
} from "@xop-any-time/shared";

/**
 * The rules behind "request a quote", as pure functions.
 *
 * THEY LIVE OUTSIDE THE COMPONENT for the reason `pages/portal/priceList.ts`
 * gives for the price-list ruling: a rule that can be called with a literal
 * and asserted in three lines is a rule that stays correct. The page is then
 * layout and state, and every decision it makes is one of these.
 *
 * THERE IS NO MONEY IN THIS FILE, and that is the point (2026-09-13). A
 * reseller says what it needs; XOP says what it costs. The catalogue the form
 * reads has no price fields in it at all — `/quote-catalog` drops them at the
 * server — so there is nothing here to total, preview or leak. crm-api prices
 * the issued quote from the command-center catalogue at the moment it issues
 * it, and its arithmetic is the only one that has ever been on the document.
 */

/** crm-api's own bound on a line quantity (spec §4.1). */
export const MAX_QUANTITY = 10_000;

/** crm-api's own bound on how many lines one quote may carry. */
export const MAX_LINES = 200;

/**
 * One row of the form: a chosen catalogue item and how many.
 *
 * `itemId` is `null` while the line is still empty — a line exists on screen
 * before it names anything, which is what "add another line" has to mean.
 * `key` is a stable client-side id so React keeps focus in the right row when
 * lines above are removed.
 */
export interface QuoteLineDraft {
  key: string;
  itemId: number | null;
  /**
   * The quantity AS TYPED, not as a number.
   *
   * Clamping on every keystroke made the field impossible to correct: clearing
   * it read as 0, snapped back to 1, and the next digit landed BESIDE that 1 —
   * so replacing "1" with "8" produced 18. The raw text is what the field
   * holds; `toRequestItems` is the one place it becomes a number, and it is
   * the only place that has to be right.
   */
  quantity: string;
}

export interface RequestGroup {
  category: PriceListCategory;
  label: string;
  items: QuoteCatalogItem[];
}

let nextKey = 0;

/** A fresh empty line. The id is client-side only and never sent. */
export function blankLine(): QuoteLineDraft {
  nextKey += 1;
  return { key: `line-${nextKey}`, itemId: null, quantity: "1" };
}

/**
 * What a typed quantity MEANS on the wire: a whole number in 1..MAX_QUANTITY.
 *
 * Applied when the request is built, never while typing — see
 * `QuoteLineDraft.quantity`. An empty or nonsense field reads as 1 rather than
 * NaN, because NaN reaches the wire as `null` and comes back as a 422 about a
 * field the reseller never typed. 0 is not a way to remove a line; a line is
 * removed with its own control.
 */
export function clampQuantity(value: number | string): number {
  const parsed = typeof value === "string" ? Number(value.trim()) : value;
  if (!Number.isFinite(parsed)) return 1;
  return Math.min(MAX_QUANTITY, Math.max(1, Math.round(parsed)));
}

/** The unit a quantity is counted in, ready to print: "per port", "each". */
export function unitLabelFor(item: QuoteCatalogItem | undefined): string {
  if (!item) return "";
  return PRICE_LIST_UNIT_LABELS[item.unit] ?? item.unit;
}

/** The same haystack the command center's picker searches — part number and
 * description — so the two behave identically on the same catalogue. */
export function matchesSearch(item: QuoteCatalogItem, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (needle === "") return true;
  return `${item.part_number} ${item.description}`.toLowerCase().includes(needle);
}

/**
 * The catalogue, in the printed book's own section order.
 *
 * Sections are iterated over `PRICE_LIST_CATEGORIES` rather than over whatever
 * categories happen to be present, so the order is the book's and not the
 * database's — and an empty section is dropped rather than printed as a
 * heading with nothing under it.
 */
/**
 * The heading a picker prints over a category: "Value Added Services", never
 * the storage slug `value_added_service`. A slug crm-api adds before this
 * table learns it falls back to itself rather than to nothing — a heading
 * over items beats no heading — but it is the table that is meant to be
 * complete (`PriceListCategory` is a closed union).
 */
export function categoryLabelFor(category: string): string {
  return (PRICE_LIST_CATEGORY_LABELS as Record<string, string>)[category] ?? category;
}

/**
 * The catalogue in BOOK order — every item of one category together, the
 * categories in the printed book's order — so a grouped picker draws each
 * heading once. MUI's Autocomplete groups adjacent options only: fed an
 * unsorted list it prints "Value Added Services" as many times as the category
 * is interrupted. Stable within a category (the server's own order stands).
 */
export function orderByCategory(items: readonly QuoteCatalogItem[]): QuoteCatalogItem[] {
  const rank = new Map<string, number>(PRICE_LIST_CATEGORIES.map((c, i) => [c, i]));
  return [...items].sort(
    (a, b) => (rank.get(a.category) ?? PRICE_LIST_CATEGORIES.length) - (rank.get(b.category) ?? PRICE_LIST_CATEGORIES.length),
  );
}

export function groupByCategory(
  items: readonly QuoteCatalogItem[],
  search: string,
): RequestGroup[] {
  const groups: RequestGroup[] = [];
  for (const category of PRICE_LIST_CATEGORIES) {
    const inSection = items.filter((i) => i.category === category && matchesSearch(i, search));
    if (inSection.length === 0) continue;
    groups.push({ category, label: PRICE_LIST_CATEGORY_LABELS[category], items: inSection });
  }
  return groups;
}

/**
 * The lines that are ready to send: every one that names an item.
 *
 * An empty line is DROPPED rather than refused — somebody who pressed "Add
 * item" once too often should not have to find the blank row to be allowed to
 * submit. The same item chosen twice is left as two lines: they may be
 * genuinely different (two sites, two quantities), and silently merging them
 * would change what the reseller asked for.
 */
export function toRequestItems(lines: readonly QuoteLineDraft[]): XopQuoteRequestLine[] {
  return lines
    .filter((line): line is QuoteLineDraft & { itemId: number } => line.itemId !== null)
    .map((line) => ({ priceListItemId: line.itemId, quantity: clampQuantity(line.quantity) }));
}

/**
 * Why the form cannot be submitted yet — one sentence, or null when it can.
 *
 * ONE function rather than a boolean per control, so the button's disabled
 * state and the reason a reader is shown can never disagree.
 */
export function submitBlocker(
  customerId: number | null,
  lines: readonly QuoteLineDraft[],
): string | null {
  if (customerId === null) return "Choose the customer this quote is for.";
  const items = toRequestItems(lines);
  if (items.length === 0) return "Add at least one item.";
  if (items.length > MAX_LINES) return `A quote may carry at most ${MAX_LINES} items.`;
  return null;
}
